import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { api } from '../../services/api'
import { Box, Typography, Grid, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import AppNav from '../../components/appNav';
import { validateBr } from 'js-brasil';
import { isWithinInterval, parseISO } from 'date-fns'
import { maskCPF2 } from '../../utils/mask';

export default function Checkin() {
    const { eventId } = useParams();
    const location = useLocation();
    const operationType = location.pathname.toLowerCase().includes('/checkout') ? 'Checkout' : 'Checkin';
    const [eventFiltered, setEventFiltered] = useState(null)
    const [loadError, setLoadError] = useState('')
    const [success, setSuccess] = useState('')
    const [cpf, setCpf] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [confirmData, setConfirmData] = useState(null)
    const [openConfirmModal, setOpenConfirmModal] = useState(false)

    function normalizeCpf(value = '') {
        return String(value).replace(/\D/g, '')
    }

    async function getEventById(id) {
        try {
            const eventById = await api.get('/events/' + id + '?populate=*')
            const eventData = eventById?.data?.data?.attributes ? eventById.data.data : eventById.data
            setEventFiltered(eventData)

            const { date_initial, date_finish } = eventData || {}
            if (date_initial && date_finish) {
                const isCheckin = isWithinInterval(new Date(), {
                    start: parseISO(date_initial),
                    end: parseISO(date_finish)
                })

                if (!isCheckin) {
                    setLoadError(`Evento fora do período permitido para ${operationType.toLowerCase()}.`)
                }
            }
        } catch (error) {
            setLoadError(`Evento não encontrado ou indisponível para ${operationType.toLowerCase()}.`)
            console.log(error)
        }
    }

    async function validateSubscriberForCheckin(document) {
        const cleanCpf = normalizeCpf(document)

        if (!validateBr.cpf(cleanCpf)) {
            throw new Error('CPF inválido')
        }

        const { data } = await api.post('/subscriber/form', {
            data: {
                cpf: document,
                event: eventId,
            }
        })

        if (!data) {
            throw new Error(`Nenhuma inscrição encontrada para este CPF neste evento.`)
        }

        return data
    }

    async function handleCheckin() {
        const cleanCpf = normalizeCpf(cpf)

        if (!validateBr.cpf(cleanCpf)) {
            setLoadError('CPF inválido')
            setSuccess('')
            return
        }

        setSubmitting(true)
        setLoadError('')
        setSuccess('')

        try {
            const subscriberData = await validateSubscriberForCheckin(cpf)
            const subscriberId = subscriberData?.id

            if (!subscriberId) {
                throw new Error(`Inscrição válida, mas não foi possível identificar o subscriber para registrar o ${operationType.toLowerCase()}.`)
            }

            const register = await api.post('/frequency/public-reader', {
                data: {
                    event: eventId,
                    id: subscriberId,
                    readAt: new Date().toISOString(),
                    type: operationType,
                }
            })

            if (register.status === 200 || register.status === 201) {
                const nextConfirmData = {
                    name: subscriberData?.name || 'N/A',
                    document: subscriberData?.document || cleanCpf,
                    email: subscriberData?.email || 'N/A',
                    whatsapp: subscriberData?.whatsapp || 'N/A',
                    event: eventFiltered?.name || eventFiltered?.attributes?.name || 'Evento',
                    type: operationType,
                }

                setConfirmData(nextConfirmData)
                setOpenConfirmModal(true)
                setSuccess(`${operationType} realizado com sucesso!`)
                setCpf('')

                setTimeout(() => {
                    setOpenConfirmModal(false)
                    setConfirmData(null)
                }, 5000)
            }
        } catch (error) {
            const message =
                error?.response?.data?.error?.message ||
                error?.response?.data?.message ||
                error?.message ||
                `Não foi possível realizar o ${operationType.toLowerCase()}.`

            setLoadError(message)
        } finally {
            setSubmitting(false)
        }
    }

    useEffect(() => {
        if (eventId) {
            getEventById(eventId)
        }
    }, [eventId])

    return (
        <Box sx={{ width: '100vw', height: '100vh', px: 0, m: 0 }}>
            <AppNav />
            <Grid container my={10} columns={12} sx={{ justifyContent: 'center' }}>
                <Grid item sm={12} px={2} sx={{ display: 'flex', justifyContent: 'center', alignText: 'center' }}>
                    <Typography>
                        <span style={{ fontSize: 22 }}>{operationType === 'Checkout' ? 'Check-out do Evento:' : 'Check-in do Evento:'}</span>
                        <br />
                        {eventFiltered?.name || eventFiltered?.attributes?.name || 'Carregando evento...'}
                    </Typography>
                </Grid>
                <Grid item sm={12} py={3} sx={{ display: 'flex', justifyContent: 'center', alignText: 'center' }}>
                    <Typography color={'green'}>{success}</Typography>
                </Grid>
                <Grid item sm={12} py={3} sx={{ display: 'flex', justifyContent: 'center', alignText: 'center' }}>
                    <Typography color={'red'}>{loadError}</Typography>
                </Grid>
            </Grid>

            <form onSubmit={(event) => {
                event.preventDefault()
                handleCheckin()
            }}>
                <Grid container direction={'column'} px={5} columns={10} spacing={2}>
                    <Grid item xs={12} md={4} sx={{ mx: 'auto' }}>
                        <TextField
                            id="cpf"
                            variant="outlined"
                            label="CPF do inscrito"
                            name="cpf"
                            fullWidth
                            value={cpf}
                            onChange={(event) => setCpf(maskCPF2(event.target.value))}
                            error={Boolean(loadError && loadError.toLowerCase().includes('cpf'))}
                            helperText={loadError || `Digite o CPF para buscar a inscrição e registrar o ${operationType.toLowerCase()}.`}
                            inputProps={{ maxLength: 14 }}
                            autoFocus
                        />
                    </Grid>

                    <Grid item xs={12} md={4} sx={{ mx: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            type={'submit'}
                            disabled={submitting || normalizeCpf(cpf).length !== 11}
                            sx={{ mt: 1 }}
                        >
                            {submitting ? 'Buscando...' : `Buscar e fazer ${operationType.toLowerCase()}`}
                        </Button>
                    </Grid>
                </Grid>
            </form>

            <Dialog open={openConfirmModal} onClose={() => setOpenConfirmModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: 'success.main', fontWeight: 700 }}>{operationType === 'Checkout' ? 'Check-out confirmado' : 'Check-in confirmado'}</DialogTitle>
                <DialogContent dividers>
                    {confirmData ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography><strong>Nome:</strong> {confirmData.name}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography><strong>CPF:</strong> {confirmData.document}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography><strong>E-mail:</strong> {confirmData.email}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography><strong>WhatsApp:</strong> {confirmData.whatsapp}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography><strong>Evento:</strong> {confirmData.event}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography><strong>Operação:</strong> {confirmData.type}</Typography>
                            </Grid>
                        </Grid>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirmModal(false)} color="success" variant="contained">
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}